import type { SourcePublic } from '../../shared/contracts'
import type { AuthMethod, AuthUser } from '@/services/api'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, ApiError } from '@/services/api'

const CONFIG_RETRY_DELAYS_MS = [500, 1_000, 2_000]

function isRetryableConfigError(error: unknown) {
    return !(error instanceof ApiError) || error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500
}

async function loadConfigWithRetry() {
    for (let attempt = 0; ; attempt += 1) {
        try { return await api.config() }
        catch (error) {
            const delay = CONFIG_RETRY_DELAYS_MS[attempt]
            if (!isRetryableConfigError(error) || delay === undefined) { throw error }
            await new Promise<void>(resolve => window.setTimeout(resolve, delay))
        }
    }
}

export const useAppStore = defineStore('app', () => {
    const name = ref('CineFlow')
    const sources = ref<SourcePublic[]>([])
    const database = ref(false)
    const authenticated = ref(false)
    const setupRequired = ref(false)
    const user = ref<AuthUser | null>(null)
    const authMethod = ref<AuthMethod | null>(null)
    const authenticatedAt = ref<number | null>(null)
    const initialized = ref(false)
    const offline = ref(!navigator.onLine)
    let initializePromise: Promise<void> | undefined

    const activeSources = computed(() => sources.value.filter(source => source.enabled))
    const isOwner = computed(() => user.value?.role === 'owner')

    window.addEventListener('online', () => offline.value = false)
    window.addEventListener('offline', () => offline.value = true)

    function initialize(force = false) {
        if (initialized.value && !force) { return Promise.resolve() }
        if (initializePromise) { return initializePromise }
        const task = (async () => {
            try {
                const config = await loadConfigWithRetry()
                name.value = config.name
                sources.value = config.sources
                database.value = config.database
                authenticated.value = config.authenticated
                setupRequired.value = config.setupRequired
                user.value = config.user
                authMethod.value = config.authMethod
                authenticatedAt.value = config.authenticatedAt
            }
            catch { /* Keep the last usable session and source snapshot while offline. */ }
            finally {
                initialized.value = true
            }
        })()
        initializePromise = task
        return task.finally(() => {
            if (initializePromise === task) { initializePromise = undefined }
        })
    }

    function applyAuth(authUser: AuthUser, method: AuthMethod, verifiedAt: number) {
        user.value = authUser
        authMethod.value = method
        authenticatedAt.value = verifiedAt
        authenticated.value = true
        setupRequired.value = false
    }

    function clearAuth() {
        sources.value = []
        user.value = null
        authMethod.value = null
        authenticatedAt.value = null
        authenticated.value = false
    }

    function applyProfile(profile: AuthUser) {
        if (user.value?.id === profile.id) { user.value = profile }
    }

    return { name, sources, activeSources, database, authenticated, setupRequired, user, authMethod, authenticatedAt, isOwner, initialized, offline, initialize, applyAuth, applyProfile, clearAuth }
})
